import { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import * as Sentry from '@sentry/react'

import { optimoPrivateApi } from '../axiosInstance'

import { setAuthToken, clearAllTokens } from '@/utils/auth/tokenManager'
import { isImpersonationActive, clearImpersonationFlag } from '@/utils/auth/impersonationSession'
import { scrubEventRecursively } from '@/lib/sentry'
import { refreshTokenDirect } from '@/api/actions/auth/authActions'
import { store } from '@/store/store'
import { clearAuth } from '@/store/slices/authSlice'
import { logger } from '@/utils/logger'
import type { RootState } from '@/store/store'
import { NavigationRoutes } from '@/router/navigation.types'

// Interface for response data that might contain onboarding completion flags
interface IResponseDataWithOnboardingFlags {
    first_time_onboarding_complete?: boolean
    first_time_hris_complete?: boolean
    [key: string]: unknown
}

// Token refresh state management
let isRefreshing = false
let failedQueue: Array<{
    resolve: (value: unknown) => void
    reject: (reason?: unknown) => void
}> = []

const processQueue = (error: unknown, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error)
        } else {
            prom.resolve(token)
        }
    })
    failedQueue = []
}

// Track retry attempts per request
const retryAttemptsMap = new Map<string, number>()
const MAX_RETRY_ATTEMPTS = 2 // Best practice: 2-3 retries max

const HAS_STRUCTURED_CLONE = typeof structuredClone === 'function'

const cloneForScrubbing = <T>(data: T): T | undefined => {
    if (data === undefined || data === null) {
        return undefined
    }

    try {
        if (HAS_STRUCTURED_CLONE) {
            return structuredClone(data)
        }
    } catch {
        // structuredClone not available or failed
    }

    try {
        return JSON.parse(JSON.stringify(data)) as T
    } catch {
        return undefined
    }
}

const captureAxiosError = (error: unknown) => {
    if (!error) {
        return
    }

    if (error instanceof AxiosError) {
        Sentry.withScope((scope) => {
            scope.setTag('error_source', 'api_request')
            if (error.config?.url) {
                scope.setTag('backend_endpoint', error.config.url)
            }

            scope.setContext('axios_request', {
                method: error.config?.method,
                url: error.config?.url,
                baseURL: error.config?.baseURL,
                retryCount: (error.config as { _retryCount?: number })?._retryCount ?? undefined,
            })

            if (error.response) {
                const responseContext = {
                    status: error.response.status,
                    statusText: error.response.statusText,
                    data: cloneForScrubbing(error.response.data),
                    headers: cloneForScrubbing(error.response.headers),
                }
                scope.setContext('django_response', scrubEventRecursively(responseContext))
            }

            Sentry.captureException(error)
        })
        return
    }

    Sentry.captureException(error)
}

const rejectWithCapture = (error: AxiosError) => {
    captureAxiosError(error)
    return Promise.reject(error)
}

export const responseSuccessInterceptor = (response: AxiosResponse) => {
    // Clear retry count on successful response
    const requestKey = `${response.config.method}-${response.config.url}`
    retryAttemptsMap.delete(requestKey)
    try {
        const data = response?.data as IResponseDataWithOnboardingFlags
        if (data && (data.first_time_onboarding_complete || data.first_time_hris_complete)) {
            const state = store.getState() as RootState
            const orgUuid = state.auth.organization?.uuid || 'org'

            if (data.first_time_onboarding_complete) {
                sessionStorage.setItem(`onboard:pending:${orgUuid}`, 'org')
                sessionStorage.setItem(`onboard:banner:${orgUuid}`, 'org')
            }

            // We no longer show a congrats banner for HRIS completion —
            // only for full organization onboarding (welcome_complete).
            // Keep room for other flows but do not set banners for HRIS here.

            // Redirect to dashboard if not already there, but do NOT
            // interrupt the HR Intake wizard (let it proceed to
            // notifications/connect steps as designed).
            if (window && typeof window !== 'undefined') {
                const path = window.location.pathname || ''
                const isInIntakeWizard = path.startsWith('/intake')
                if (!path.startsWith('/dashboard') && !isInIntakeWizard) {
                    window.location.replace('/dashboard')
                }
            }
        }
    } catch (e) {
        // Non-fatal UI enhancement
        logger.warn('[Axios] onboarding-first-time hook failed', { error: e })
    }
    return response
}

export const responseFailureInterceptor = async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean; _retryCount?: number }

    if (!originalRequest) {
        return rejectWithCapture(error)
    }

    // Create a unique key for this request
    const requestKey = `${originalRequest.method}-${originalRequest.url}`

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
        // If this is an impersonation session, do not attempt a refresh.
        // Exchange-only impersonation tokens cannot be refreshed; skip directly to logout flow.
        if (isImpersonationActive()) {
            retryAttemptsMap.delete(requestKey)
            // Important: clear the impersonation flag so subsequent
            // real logins in the same tab do not continue to behave
            // as if impersonation is active (which would otherwise
            // skip token refresh and cause immediate logout).
            clearImpersonationFlag()
            clearAllTokens()
            store.dispatch(clearAuth())
            if (!window.location.pathname.includes('/auth/login')) {
                window.location.href = NavigationRoutes.LOGIN
            }
            return rejectWithCapture(error)
        }
        // Skip retry for auth endpoints to prevent loops
        const authEndpoints = [
            '/auth/check',
            '/auth/signup',
            '/auth/exchange-magic-token',
            '/auth/refresh',
            '/auth/refresh-token', // Direct refresh call without interceptors
            '/auth/logout',
            '/auth/logout-all',
        ]
        const isAuthEndpoint = authEndpoints.some((endpoint) => originalRequest.url?.includes(endpoint))

        if (isAuthEndpoint) {
            logger.error('[Axios] Auth endpoint failed with 401', { url: originalRequest.url })
            retryAttemptsMap.delete(requestKey)
            clearAllTokens()
            store.dispatch(clearAuth())

            // Only redirect if not already on login page
            if (!window.location.pathname.includes('/auth/login')) {
                window.location.href = NavigationRoutes.LOGIN
            }
            return rejectWithCapture(error)
        }

        // Check retry count
        const retryCount = originalRequest._retryCount || 0

        // If already retried maximum times, give up
        if (retryCount >= MAX_RETRY_ATTEMPTS) {
            logger.error('[Axios] Max retry attempts reached', {
                url: originalRequest.url,
                retryCount,
                maxRetries: MAX_RETRY_ATTEMPTS,
            })
            retryAttemptsMap.delete(requestKey)
            clearAllTokens()
            store.dispatch(clearAuth())
            window.location.href = '/auth/login'
            return rejectWithCapture(error)
        }

        // Prevent multiple retries of the same request
        if (originalRequest._retry) {
            return rejectWithCapture(error)
        }

        // If already refreshing, queue the request
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject })
            })
                .then((token) => {
                    originalRequest.headers.Authorization = `Bearer ${token}`
                    return optimoPrivateApi(originalRequest)
                })
                .catch((err) => {
                    captureAxiosError(err)
                    return Promise.reject(err)
                })
        }

        originalRequest._retry = true
        originalRequest._retryCount = retryCount + 1

        // If already refreshing, queue the request
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject })
            })
                .then((token) => {
                    originalRequest.headers.Authorization = `Bearer ${token}`
                    return optimoPrivateApi(originalRequest)
                })
                .catch((err) => {
                    captureAxiosError(err)
                    return Promise.reject(err)
                })
        }

        // Start refreshing
        isRefreshing = true

        logger.info('[Axios] Starting token refresh')

        return new Promise((resolve, reject) => {
            refreshTokenDirect()
                .then((refreshResponse) => {
                    const { access_token, refresh_token } = refreshResponse

                    logger.info('[Axios] Token refresh successful')

                    // Update tokens
                    setAuthToken(access_token, 'access')
                    setAuthToken(refresh_token, 'refresh')

                    // Update default headers
                    optimoPrivateApi.defaults.headers.common['Authorization'] = `Bearer ${access_token}`

                    // Update original request
                    originalRequest.headers.Authorization = `Bearer ${access_token}`

                    // Clear retry count for this request
                    retryAttemptsMap.delete(requestKey)

                    // Process queued requests
                    processQueue(null, access_token)

                    // Retry original request
                    resolve(optimoPrivateApi(originalRequest))
                })
                .catch((refreshError) => {
                    logger.error('[Axios] Token refresh failed', { error: refreshError })

                    // Clear auth and redirect to login on refresh failure
                    processQueue(refreshError, null)
                    clearAllTokens()
                    store.dispatch(clearAuth())

                    // Only redirect if not already on login/error page
                    if (!window.location.pathname.includes('/auth/')) {
                        window.location.href = NavigationRoutes.LOGIN
                    }

                    captureAxiosError(refreshError)
                    reject(refreshError)
                })
                .finally(() => {
                    isRefreshing = false
                })
        })
    }

    // For non-401 errors, just pass through
    return rejectWithCapture(error)
}
